import { useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { LocaleContext } from '../../context/LocaleContext';
import { Spinner } from '../../shared/ui/spinner';
import {
	AlertTriangle,
	Calendar,
	Camera,
	DollarSign,
	Eye,
	EyeOff,
	Globe,
	Lock,
	Mail,
	Shield,
	Upload,
	User,
	X,
} from 'lucide-react';
import DatePicker from 'react-datepicker';

import { datePickerLocale } from '../../shared/i18n/date-picker-locale';
import {
	changePassword,
	deleteProfilePicture,
	disableEmailMFA,
	disableTOTP,
	getMFAStatus,
	setupEmailMFA,
	setupTOTP,
	updateProfile,
	uploadProfilePicture,
	verifyEmailMFASetup,
	verifyTOTPSetup,
} from './api';
import { useAuth } from '../../context/AuthContext';
import { SUPPORTED_CURRENCIES_WITH_FLAGS } from '../../constants/currencies';

import 'react-datepicker/dist/react-datepicker.css';
import './calendarTheme.css';
import styles from './Profile.module.css';

// Range the profile date-of-birth calendar allows: 1900 up to today.
const BIRTH_MIN_DATE = new Date(1900, 0, 1);

/** "YYYY-MM-DD" -> local Date (no UTC shift), or null when unset/invalid. */
const parseBirthDate = dateString => {
	if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString || '')) return null;
	const [year, month, day] = dateString.split('-').map(Number);
	const date = new Date(year, month - 1, day);
	return isNaN(date.getTime()) ? null : date;
};

/** Date -> "YYYY-MM-DD" using local parts, so the picked day is the saved day. */
const formatBirthDate = date => {
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${date.getFullYear()}-${month}-${day}`;
};

const Profile = () => {
	const { handleSetLocale } = useContext(LocaleContext);
	const intl = useIntl();

	const { fetchMe, userInfo: currentUser } = useAuth();

	// Check if user logged in via Google
	const isGoogleUser = () => {
		return window.localStorage.getItem('authProvider') === 'google';
	};

	// Individual field edit modal states
	const [showEditNameModal, setShowEditNameModal] = useState(false);
	const [showEditDateOfBirthModal, setShowEditDateOfBirthModal] =
		useState(false);
	const [showEditLanguageModal, setShowEditLanguageModal] = useState(false);
	const [showEditCurrencyModal, setShowEditCurrencyModal] = useState(false);
	const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

	// Field-specific edit values
	const [editNameValue, setEditNameValue] = useState('');
	const [editBirthDate, setEditBirthDate] = useState(null);
	const [editLanguageValue, setEditLanguageValue] = useState('');
	const [editCurrencyValue, setEditCurrencyValue] = useState('');

	// Password change fields
	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [showCurrentPassword, setShowCurrentPassword] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [passwordErrors, setPasswordErrors] = useState({});

	// Field update loading states
	const [updatingField, setUpdatingField] = useState(null);
	const [fieldWithError, setFieldWithError] = useState(null);
	const [mfaStatus, setMfaStatus] = useState({
		totp_enabled: false,
		email_mfa_enabled: false,
		mfa_required: false,
		backup_codes_remaining: 0,
	});
	const [loadingMFA, setLoadingMFA] = useState(false);
	const [mfaError, setMfaError] = useState('');
	const [mfaSuccess, setMfaSuccess] = useState('');
	const [showMFASetup, setShowMFASetup] = useState(false);
	const [mfaToggleState, setMfaToggleState] = useState(false);
	const [userToggledMFA, setUserToggledMFA] = useState(false); // Track if user explicitly toggled

	const [saveError, setSaveError] = useState('');
	const [saveSuccess, setSaveSuccess] = useState('');

	// TOTP Setup State
	const [showTOTPSetup, setShowTOTPSetup] = useState(false);
	const [totpSetupData, setTotpSetupData] = useState(null);
	const [totpVerifyCode, setTotpVerifyCode] = useState('');
	const [totpBackupCodes, setTotpBackupCodes] = useState(null);

	// TOTP Disable State
	const [showTOTPDisable, setShowTOTPDisable] = useState(false);
	const [totpDisableCode, setTotpDisableCode] = useState('');
	const [totpDisableCodeInputs, setTotpDisableCodeInputs] = useState([
		'',
		'',
		'',
		'',
		'',
		'',
	]);

	// Email MFA State
	const [loadingEmailMFA, setLoadingEmailMFA] = useState(false);
	const [showEmailMFASetup, setShowEmailMFASetup] = useState(false);
	const [emailMFASetupCode, setEmailMFASetupCode] = useState('');

	// New MFA Flow State
	const [showMFAMethodSelection, setShowMFAMethodSelection] = useState(false);
	const [selectedMFAMethod, setSelectedMFAMethod] = useState(null); // 'email' or 'totp'
	const [emailResendCountdown, setEmailResendCountdown] = useState(0);
	const [showSecretKey, setShowSecretKey] = useState(false);
	const [showEmailSuccess, setShowEmailSuccess] = useState(false);
	const [showTOTPSuccess, setShowTOTPSuccess] = useState(false);
	const [showTOTPVerification, setShowTOTPVerification] = useState(false);
	const [codeInputs, setCodeInputs] = useState(['', '', '', '', '', '']);

	const [showImageModal, setShowImageModal] = useState(false);
	const [selectedImage, setSelectedImage] = useState(null);
	const [imagePreview, setImagePreview] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isDeletingPicture, setIsDeletingPicture] = useState(false);
	const [isUploadingPicture, setIsUploadingPicture] = useState(false);
	const [showDeletePhotoConfirm, setShowDeletePhotoConfirm] = useState(false);
	const fileInputRef = useRef(null);
	const hasLoadedData = useRef(false);
	const mfaSectionRef = useRef(null);

	const [formData, setFormData] = useState({
		fullName: '',
		login: '',
		email: '',
		password: '',
		dateOfBirth: '',
		language: '',
		currency: '',
	});

	const languageMap = {
		English: 'en',
		Armenian: 'hy',
		Russian: 'ru',
		en: 'en',
		hy: 'hy',
		ru: 'ru',
	};

	// Helper function to format date to YYYY-MM-DD
	const formatDateToYYYYMMDD = dateString => {
		if (!dateString) return '';

		// If already in YYYY-MM-DD format, return as is
		if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
			return dateString;
		}

		// Try to parse the date and format it
		try {
			// If it's a date-only string (YYYY-MM-DD), parse it directly without timezone conversion
			if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
				// Extract just the date part (in case there's time info)
				const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
				if (dateMatch) {
					return `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`;
				}
			}

			// For other formats, use Date but with UTC methods to avoid timezone issues
			const date = new Date(dateString);
			if (isNaN(date.getTime())) return '';

			// Use UTC methods to avoid timezone conversion
			const year = date.getUTCFullYear();
			const month = String(date.getUTCMonth() + 1).padStart(2, '0');
			const day = String(date.getUTCDate()).padStart(2, '0');

			return `${year}-${month}-${day}`;
		} catch (error) {
			return '';
		}
	};

	// Fetch user data on component mount
	useEffect(() => {
		// Prevent infinite loop - only load once
		if (hasLoadedData.current) {
			return;
		}

		const loadUserData = async () => {
			try {
				setIsLoading(true);

				// Check if we have a token first
				const token = localStorage.getItem('accessToken');
				if (!token) {
					console.error('No authentication token found');
					setIsLoading(false);
					return;
				}

				// Try to get user data from context first
				let userData = currentUser;

				// If no user data in context, try to fetch it
				if (!userData) {
					userData = await fetchMe();
				}

				if (userData) {
					// Map language from backend format (en/hy/ru) to display format
					const langPref = userData.language_preference || 'en';
					const languageDisplay =
						langPref === 'en'
							? 'English'
							: langPref === 'hy'
								? 'Armenian'
								: langPref === 'ru'
									? 'Russian'
									: 'English';

					setFormData({
						fullName: userData.full_name || '',
						login: userData.email || '',
						email: userData.email || '',
						dateOfBirth: formatDateToYYYYMMDD(userData.date_of_birth) || '',
						language: languageDisplay,
						currency: userData.currency_preference || 'USD',
						password: '',
					});
					hasLoadedData.current = true;
				} else {
					console.warn('No user data available');
				}
			} catch (error) {
				console.error('Failed to load user data:', error);
			} finally {
				setIsLoading(false);
			}
		};

		loadUserData();
	}, []);

	useEffect(() => {
		if (currentUser && hasLoadedData.current) {
			const langPref = currentUser.language_preference || 'en';
			const languageDisplay =
				langPref === 'en'
					? 'English'
					: langPref === 'hy'
						? 'Armenian'
						: langPref === 'ru'
							? 'Russian'
							: 'English';

			setFormData(prev => ({
				...prev,
				fullName: currentUser.full_name || prev.fullName,
				login: currentUser.email || prev.login,
				email: currentUser.email || prev.email,
				dateOfBirth:
					formatDateToYYYYMMDD(currentUser.date_of_birth) || prev.dateOfBirth,
				language: languageDisplay || prev.language,
				currency: currentUser.currency_preference || prev.currency,
			}));
		}
	}, [currentUser]);

	// Load MFA status on component mount
	useEffect(() => {
		// CRITICAL: Always start with MFA section hidden
		// It will only show if user clicks or if mfa_required is true
		setShowMFASetup(false);
		setMfaToggleState(false);
		setUserToggledMFA(false); // Reset toggle tracking on mount

		const loadMFA = async () => {
			const token = localStorage.getItem('accessToken');
			if (token) {
				await loadMFAStatus();
			}
		};
		loadMFA();
	}, []); // Only run once on mount

	// Note: MFA section visibility is now handled entirely in loadMFAStatus
	// to avoid race conditions with useEffect

	// Scroll to MFA section when it becomes visible
	useEffect(() => {
		if (showMFASetup && mfaSectionRef.current) {
			// Use a longer timeout to ensure the section is fully rendered
			const timer = setTimeout(() => {
				if (mfaSectionRef.current) {
					mfaSectionRef.current.scrollIntoView({
						behavior: 'smooth',
						block: 'start',
						inline: 'nearest',
					});
				}
			}, 300);
			return () => clearTimeout(timer);
		}
	}, [showMFASetup]);

	// Email resend countdown timer
	useEffect(() => {
		let timer;
		if (emailResendCountdown > 0) {
			timer = setInterval(() => {
				setEmailResendCountdown(prev => {
					if (prev <= 1) {
						return 0;
					}
					return prev - 1;
				});
			}, 1000);
		}
		return () => {
			if (timer) clearInterval(timer);
		};
	}, [emailResendCountdown]);

	const loadMFAStatus = async () => {
		try {
			const result = await getMFAStatus();
			if (result && !result.response) {
				// Set MFA status first
				setMfaStatus(result);

				// CRITICAL: Only hide MFA section if user hasn't explicitly toggled it
				// If user clicked to show it, preserve that state
				if (!userToggledMFA) {
					setShowMFASetup(false);
					setMfaToggleState(false);
				} else {
					// Don't change showMFASetup - preserve user's choice
				}
			}
		} catch (error) {
			console.error('Failed to load MFA status:', error);
			// On error, only hide if user hasn't toggled
			if (!userToggledMFA) {
				setShowMFASetup(false);
				setMfaToggleState(false);
			}
		}
	};

	const handleMFAToggle = () => {
		// Mark that user has explicitly toggled MFA
		setUserToggledMFA(true);

		// Check if MFA is currently enabled
		const isMFAEnabled = mfaStatus.totp_enabled || mfaStatus.email_mfa_enabled;

		// If MFA is not enabled and user is turning the toggle ON, show method selection
		// But only if MFA is not required (if required, just toggle visibility)
		if (!isMFAEnabled && !mfaToggleState && !mfaStatus.mfa_required) {
			handleEnableMFA();
			return;
		}

		// If MFA is enabled or required, just toggle the section visibility
		const newState = !showMFASetup;
		setShowMFASetup(newState);
		setMfaToggleState(newState);
		// Note: Scrolling is handled by useEffect when showMFASetup changes
	};

	// New MFA Flow Handlers
	const handleEnableMFA = () => {
		setShowMFAMethodSelection(true);
		setSelectedMFAMethod(null);
		setMfaError('');
		setMfaSuccess('');
	};

	const handleEnableEmailMFADirect = async () => {
		setSelectedMFAMethod('email');
		setMfaError('');
		setMfaSuccess('');
		setLoadingEmailMFA(true);
		try {
			const result = await setupEmailMFA();
			if (result && result.response) {
				const errorData = result.response?.data?.detail;
				setMfaError(
					typeof errorData === 'string'
						? errorData
						: intl.formatMessage({ id: 'ProfileErrorID1' }),
				);
				setLoadingEmailMFA(false);
				setSelectedMFAMethod(null);
				return;
			}
			if (result && result.success) {
				setShowEmailMFASetup(true);
				setEmailMFASetupCode('');
				setCodeInputs(['', '', '', '', '', '']);
				setEmailResendCountdown(60);
			}
		} catch (error) {
			setMfaError(intl.formatMessage({ id: 'ProfileErrorID2' }));
			setSelectedMFAMethod(null);
		} finally {
			setLoadingEmailMFA(false);
		}
	};

	const handleEnableTOTPDirect = async () => {
		setSelectedMFAMethod('totp');
		setMfaError('');
		setMfaSuccess('');
		setLoadingMFA(true);
		try {
			const result = await setupTOTP();
			if (result && result.response) {
				setMfaError(intl.formatMessage({ id: 'ProfileErrorID3' }));
				setLoadingMFA(false);
				setSelectedMFAMethod(null);
				return;
			}
			if (result) {
				setTotpSetupData(result);
				setShowTOTPSetup(true);
				setShowTOTPVerification(false);
				setTotpVerifyCode('');
				setCodeInputs(['', '', '', '', '', '']);
				setShowSecretKey(false);
			}
		} catch (error) {
			setMfaError(intl.formatMessage({ id: 'ProfileErrorID4' }));
			setSelectedMFAMethod(null);
		} finally {
			setLoadingMFA(false);
		}
	};

	const handleSelectMFAMethod = async method => {
		setSelectedMFAMethod(method);
		setMfaError('');
		setMfaSuccess('');

		if (method === 'email') {
			// Start email MFA setup
			setLoadingEmailMFA(true);
			try {
				const result = await setupEmailMFA();
				if (result && result.response) {
					const errorData = result.response?.data?.detail;
					setMfaError(
						typeof errorData === 'string'
							? errorData
							: intl.formatMessage({ id: 'ProfileErrorID5' }),
					);
					setLoadingEmailMFA(false);
					setSelectedMFAMethod(null);
					// Keep method selection modal open on error
					return;
				}
				if (result && result.success) {
					// Close method selection and open email verification
					setShowMFAMethodSelection(false);
					setShowEmailMFASetup(true);
					setEmailMFASetupCode('');
					setCodeInputs(['', '', '', '', '', '']);
					setEmailResendCountdown(60);
				}
			} catch (error) {
				setMfaError(intl.formatMessage({ id: 'ProfileErrorID6' }));
				setSelectedMFAMethod(null);
			} finally {
				setLoadingEmailMFA(false);
			}
		} else if (method === 'totp') {
			// Start TOTP setup
			setLoadingMFA(true);
			try {
				const result = await setupTOTP();
				if (result && result.response) {
					setMfaError(intl.formatMessage({ id: 'ProfileErrorID7' }));
					setLoadingMFA(false);
					setSelectedMFAMethod(null);
					// Keep method selection modal open on error
					return;
				}
				if (result) {
					// Close method selection and open TOTP setup
					setShowMFAMethodSelection(false);
					setTotpSetupData(result);
					setShowTOTPSetup(true);
					setShowTOTPVerification(false);
					setTotpVerifyCode('');
					setCodeInputs(['', '', '', '', '', '']);
					setShowSecretKey(false);
				}
			} catch (error) {
				setMfaError(intl.formatMessage({ id: 'ProfileErrorID8' }));
				setSelectedMFAMethod(null);
			} finally {
				setLoadingMFA(false);
			}
		}
	};

	const handleResendEmailCode = async () => {
		if (emailResendCountdown > 0) return;

		setLoadingEmailMFA(true);
		setMfaError('');
		try {
			const result = await setupEmailMFA();
			if (result && result.response) {
				const errorData = result.response?.data?.detail;
				setMfaError(
					typeof errorData === 'string'
						? errorData
						: intl.formatMessage({ id: 'ProfileErrorID9' }),
				);
				setLoadingEmailMFA(false);
				return;
			}
			if (result && result.success) {
				setEmailResendCountdown(60);
				setMfaSuccess(intl.formatMessage({ id: 'ProfileSuccessID10' }));
				setTimeout(() => setMfaSuccess(''), 3000);
			}
		} catch (error) {
			setMfaError(intl.formatMessage({ id: 'ProfileErrorID11' }));
		} finally {
			setLoadingEmailMFA(false);
		}
	};

	const handleCodeInputChange = (index, value) => {
		// Only allow digits
		const digit = value.replace(/\D/g, '').slice(0, 1);
		const newInputs = [...codeInputs];
		newInputs[index] = digit;
		setCodeInputs(newInputs);

		// Auto-focus next input
		if (digit && index < 5) {
			const nextInput = document.getElementById(`code-input-${index + 1}`);
			if (nextInput) nextInput.focus();
		}

		// Update the combined code
		const combinedCode = newInputs.join('');
		if (selectedMFAMethod === 'email') {
			setEmailMFASetupCode(combinedCode);
		} else if (selectedMFAMethod === 'totp') {
			setTotpVerifyCode(combinedCode);
		}
	};

	const handleCodeInputKeyDown = (index, e) => {
		// Handle backspace
		if (e.key === 'Backspace' && !codeInputs[index] && index > 0) {
			const prevInput = document.getElementById(`code-input-${index - 1}`);
			if (prevInput) prevInput.focus();
		}
	};

	const handlePasteCode = e => {
		e.preventDefault();
		const pastedData = e.clipboardData
			.getData('text')
			.replace(/\D/g, '')
			.slice(0, 6);
		if (pastedData.length > 0) {
			const newInputs = ['', '', '', '', '', ''];
			for (let i = 0; i < pastedData.length && i < 6; i++) {
				newInputs[i] = pastedData[i];
			}
			setCodeInputs(newInputs);
			const combinedCode = newInputs.join('');
			if (selectedMFAMethod === 'email') {
				setEmailMFASetupCode(combinedCode);
			} else if (selectedMFAMethod === 'totp') {
				setTotpVerifyCode(combinedCode);
			}
			// Focus the next empty input or the last one
			const nextEmptyIndex = Math.min(pastedData.length, 5);
			const nextInput = document.getElementById(`code-input-${nextEmptyIndex}`);
			if (nextInput) nextInput.focus();
		}
	};

	const copySecretKey = () => {
		if (totpSetupData?.secret) {
			navigator.clipboard.writeText(totpSetupData.secret);
			setMfaSuccess(intl.formatMessage({ id: 'ProfileSuccessID10' }));
			setTimeout(() => setMfaSuccess(''), 2000);
		}
	};

	const handleInputChange = (field, value) => {
		setFormData(prev => ({ ...prev, [field]: value }));
	};

	const handleImageClick = () => {
		// Set current profile picture as preview when modal opens (only if no new image is selected)
		if (!selectedImage) {
			const currentPictureUrl = getProfilePictureUrl(
				currentUser?.profile_picture,
			);
			if (currentPictureUrl) {
				setImagePreview(currentPictureUrl);
			} else {
				setImagePreview(null);
			}
		}
		setShowImageModal(true);
	};

	const handleFileSelect = e => {
		const file = e.target.files?.[0];
		if (!file) {
			return;
		}

		const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
		const validExtensions = ['.jpg', '.jpeg', '.png', '.gif'];
		const maxSize = 5 * 1024 * 1024; // 5MB

		// Check file type - on mobile, file.type might be empty, so also check extension
		const fileName = file.name?.toLowerCase() || '';
		const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
		const isValidType =
			validTypes.includes(file.type) || validExtensions.includes(fileExtension);

		// Also check if it starts with "image/" as a fallback for mobile browsers
		const isImageType = file.type?.startsWith('image/') || isValidType;

		if (!isImageType) {
			alert(intl.formatMessage({ id: 'ProfileSuccessID11' }));
			// Reset input value to allow selecting again
			if (e.target) {
				e.target.value = '';
			}
			return;
		}

		if (file.size > maxSize) {
			alert(intl.formatMessage({ id: 'ProfileErrorID12' }));
			// Reset input value to allow selecting again
			if (e.target) {
				e.target.value = '';
			}
			return;
		}

		setSelectedImage(file);

		const reader = new FileReader();
		reader.onloadend = () => {
			setImagePreview(reader.result);
			// Reset input value after successfully reading file - this allows selecting the same file again
			// Use setTimeout to ensure it happens after the state updates
			setTimeout(() => {
				if (e.target) {
					e.target.value = '';
				}
			}, 100);
		};
		reader.onerror = () => {
			alert(intl.formatMessage({ id: 'ProfileErrorID13' }));
			setSelectedImage(null);
			setImagePreview(null);
			if (e.target) {
				e.target.value = '';
			}
		};
		reader.readAsDataURL(file);
	};

	const handleDeleteProfilePicture = () => {
		if (!currentUser?.profile_picture) {
			return;
		}
		setShowDeletePhotoConfirm(true);
	};

	const handleConfirmDeletePhoto = async () => {
		setIsDeletingPicture(true);
		try {
			await deleteProfilePicture();
			// Refresh user data to get updated profile
			await fetchMe();
			// Clear preview and selected image
			setImagePreview(null);
			setSelectedImage(null);
			setShowImageModal(false);
			setShowDeletePhotoConfirm(false);
			setSaveSuccess(intl.formatMessage({ id: 'ProfileSuccessID14' }));
			setTimeout(() => setSaveSuccess(''), 3000);
		} catch (error) {
			console.error('Delete profile picture error:', error);
			const errorMessage =
				error.response?.data?.detail ||
				error.message ||
				intl.formatMessage({ id: 'ProfileErrorID15' });
			setSaveError(errorMessage);
			setTimeout(() => setSaveError(''), 3000);
		} finally {
			setIsDeletingPicture(false);
		}
	};

	const handleCancelDeletePhoto = () => {
		setShowDeletePhotoConfirm(false);
	};

	const handleDoneInModal = async () => {
		// If a new image is selected, upload it immediately
		if (selectedImage) {
			setIsUploadingPicture(true);
			try {
				const uploadResult = await uploadProfilePicture(selectedImage);
				if (uploadResult && uploadResult.profile_picture) {
					// Profile picture is already updated on backend, just refresh user data
					await fetchMe();
					setSelectedImage(null);
					// Update preview to show the newly uploaded photo
					const newPictureUrl = getProfilePictureUrl(
						uploadResult.profile_picture,
					);
					setImagePreview(newPictureUrl || null);
					setShowImageModal(false);
					setSaveSuccess(intl.formatMessage({ id: 'ProfileSuccessID16' }));
					setTimeout(() => setSaveSuccess(''), 3000);
				}
			} catch (uploadError) {
				console.error('Profile picture upload error:', uploadError);
				const errorMessage =
					uploadError.response?.data?.detail ||
					uploadError.message ||
					intl.formatMessage({ id: 'ProfileErrorID17' });
				setSaveError(errorMessage);
				setTimeout(() => setSaveError(''), 3000);
				// Don't close modal on error so user can try again
			} finally {
				setIsUploadingPicture(false);
			}
		} else {
			// No new image selected, just close the modal
			setShowImageModal(false);
		}
	};

	// Helper function to get full image URL
	const getProfilePictureUrl = profilePicture => {
		if (!profilePicture) return null;

		// If it's already a full URL, return as is
		if (
			profilePicture.startsWith('http://') ||
			profilePicture.startsWith('https://')
		) {
			return profilePicture;
		}

		// Get base URL without /api/v1 path
		const API_URL = process.env.REACT_APP_API_URL;
		let baseUrl = API_URL?.endsWith('/') ? API_URL.slice(0, -1) : API_URL;

		// Remove /api/v1 or /api from the end of the URL if present
		baseUrl = baseUrl.replace(/\/api\/v\d+$/, '').replace(/\/api$/, '');

		// If it's a relative path starting with /uploads/, construct full URL
		if (profilePicture.startsWith('/uploads/')) {
			return `${baseUrl}${profilePicture}`;
		}

		// If it's a relative path without /uploads/, add it
		if (!profilePicture.startsWith('/')) {
			return `${baseUrl}/uploads/${profilePicture}`;
		}

		return profilePicture;
	};

	// Helper function to generate initials from full name
	const getInitials = fullName => {
		if (!fullName || !fullName.trim()) {
			return 'U'; // Default to "U" for User
		}

		const nameParts = fullName.trim().split(/\s+/);
		if (nameParts.length === 1) {
			// Single name - return first two letters
			return nameParts[0].substring(0, 2).toUpperCase();
		} else {
			// Multiple names - return first letter of first and last name
			return (
				nameParts[0][0] + nameParts[nameParts.length - 1][0]
			).toUpperCase();
		}
	};

	const handleTOTPSetup = async () => {
		setLoadingMFA(true);
		setMfaError('');
		setMfaSuccess('');
		try {
			const result = await setupTOTP();
			if (result && result.response) {
				setMfaError(intl.formatMessage({ id: 'ProfileErrorID18' }));
				setLoadingMFA(false);
				return;
			}
			if (result) {
				setTotpSetupData(result);
				setShowTOTPSetup(true);
			}
		} catch (error) {
			setMfaError(intl.formatMessage({ id: 'ProfileErrorID19' }));
		} finally {
			setLoadingMFA(false);
		}
	};

	const handleTOTPVerify = async () => {
		const code = codeInputs.join('');
		if (!code || code.length !== 6 || !/^\d{6}$/.test(code)) {
			setMfaError(intl.formatMessage({ id: 'ProfileErrorID20' }));
			return;
		}

		setLoadingMFA(true);
		setMfaError('');
		try {
			const result = await verifyTOTPSetup(code);
			if (result && result.response) {
				const errorData = result.response?.data?.detail;
				setMfaError(
					typeof errorData === 'string'
						? errorData
						: intl.formatMessage({ id: 'ProfileErrorID21' }),
				);
				setLoadingMFA(false);
				return;
			}
			if (result && result.success) {
				setTotpBackupCodes(totpSetupData.backup_codes);
				setShowTOTPVerification(false);
				setShowTOTPSetup(false);
				setShowTOTPSuccess(true);
				setCodeInputs(['', '', '', '', '', '']);
				setTotpVerifyCode('');
				setSelectedMFAMethod(null);
				setMfaToggleState(true);
				// Don't automatically show MFA section after enabling - user must click to see it
				// setShowMFASetup(true);
				await loadMFAStatus();
			}
		} catch (error) {
			setMfaError(intl.formatMessage({ id: 'ProfileErrorID22' }));
		} finally {
			setLoadingMFA(false);
		}
	};

	const handleTOTPDisableCodeInputChange = (index, value) => {
		// Only allow digits
		const digit = value.replace(/\D/g, '').slice(0, 1);
		const newInputs = [...totpDisableCodeInputs];
		newInputs[index] = digit;
		setTotpDisableCodeInputs(newInputs);

		// Auto-focus next input
		if (digit && index < 5) {
			const nextInput = document.getElementById(
				`disable-code-input-${index + 1}`,
			);
			if (nextInput) nextInput.focus();
		}

		// Update the combined code
		const combinedCode = newInputs.join('');
		setTotpDisableCode(combinedCode);
	};

	const handleTOTPDisableCodeInputKeyDown = (index, e) => {
		// Handle backspace
		if (e.key === 'Backspace' && !totpDisableCodeInputs[index] && index > 0) {
			const prevInput = document.getElementById(
				`disable-code-input-${index - 1}`,
			);
			if (prevInput) prevInput.focus();
		}
	};

	const handleTOTPDisablePasteCode = e => {
		e.preventDefault();
		const pastedData = e.clipboardData
			.getData('text')
			.replace(/\D/g, '')
			.slice(0, 6);
		if (pastedData.length > 0) {
			const newInputs = ['', '', '', '', '', ''];
			for (let i = 0; i < pastedData.length && i < 6; i++) {
				newInputs[i] = pastedData[i];
			}
			setTotpDisableCodeInputs(newInputs);
			const combinedCode = newInputs.join('');
			setTotpDisableCode(combinedCode);
			// Focus the next empty input or the last one
			const nextEmptyIndex = Math.min(pastedData.length, 5);
			const nextInput = document.getElementById(
				`disable-code-input-${nextEmptyIndex}`,
			);
			if (nextInput) nextInput.focus();
		}
	};

	const handleTOTPDisableClick = () => {
		setShowTOTPDisable(true);
		setTotpDisableCode('');
		setTotpDisableCodeInputs(['', '', '', '', '', '']);
		setMfaError('');
	};

	const handleTOTPDisable = async () => {
		const code = totpDisableCodeInputs.join('');
		if (!code || code.length !== 6 || !/^\d{6}$/.test(code)) {
			setMfaError(intl.formatMessage({ id: 'ProfileErrorID23' }));
			return;
		}

		setLoadingMFA(true);
		setMfaError('');
		try {
			const result = await disableTOTP(code);
			if (result && result.response) {
				const errorData = result.response?.data?.detail;
				setMfaError(
					typeof errorData === 'string'
						? errorData
						: intl.formatMessage({ id: 'ProfileErrorID22' }),
				);
				setLoadingMFA(false);
				return;
			}
			if (result && result.success) {
				setMfaSuccess(intl.formatMessage({ id: 'ProfileErrorID47' }));
				setShowTOTPDisable(false);
				setTotpDisableCode('');
				setTotpDisableCodeInputs(['', '', '', '', '', '']);
				await loadMFAStatus();
				setTimeout(() => setMfaSuccess(''), 3000);
			}
		} catch (error) {
			setMfaError(intl.formatMessage({ id: 'ProfileErrorID23' }));
		} finally {
			setLoadingMFA(false);
		}
	};

	const handleEmailMFAToggle = async () => {
		if (mfaStatus.email_mfa_enabled) {
			setLoadingEmailMFA(true);
			setMfaError('');
			setMfaSuccess('');
			try {
				const result = await disableEmailMFA();
				if (result && result.response) {
					const errorData = result.response?.data?.detail;
					setMfaError(
						typeof errorData === 'string'
							? errorData
							: intl.formatMessage({ id: 'ProfileErrorID24' }),
					);
					setLoadingEmailMFA(false);
					return;
				}
				if (result && result.success) {
					setMfaSuccess(intl.formatMessage({ id: 'ProfileErrorID25' }));
					await loadMFAStatus();
					setTimeout(() => setMfaSuccess(''), 3000);
				}
			} catch (error) {
				setMfaError(intl.formatMessage({ id: 'ProfileErrorID26' }));
			} finally {
				setLoadingEmailMFA(false);
			}
		} else {
			setLoadingEmailMFA(true);
			setMfaError('');
			setMfaSuccess('');
			try {
				const result = await setupEmailMFA();
				if (result && result.response) {
					const errorData = result.response?.data?.detail;
					setMfaError(
						typeof errorData === 'string'
							? errorData
							: intl.formatMessage({ id: 'ProfileErrorID27' }),
					);
					setLoadingEmailMFA(false);
					return;
				}
				if (result && result.success) {
					setShowEmailMFASetup(true);
					setEmailMFASetupCode('');
					setMfaSuccess(intl.formatMessage({ id: 'ProfileErrorID28' }));
					setTimeout(() => setMfaSuccess(''), 3000);
				}
			} catch (error) {
				setMfaError(intl.formatMessage({ id: 'ProfileErrorID29' }));
			} finally {
				setLoadingEmailMFA(false);
			}
		}
	};

	const handleEmailMFASetupVerify = async () => {
		const code = codeInputs.join('');
		if (!code || code.length !== 6 || !/^\d{6}$/.test(code)) {
			setMfaError(intl.formatMessage({ id: 'ProfileErrorID30' }));
			return;
		}

		setLoadingEmailMFA(true);
		setMfaError('');
		try {
			const result = await verifyEmailMFASetup(code);
			if (result && result.response) {
				const errorData = result.response?.data?.detail;
				setMfaError(
					typeof errorData === 'string'
						? errorData
						: intl.formatMessage({ id: 'ProfileErrorID31' }),
				);
				setLoadingEmailMFA(false);
				return;
			}
			if (result && result.success) {
				setShowEmailMFASetup(false);
				setShowEmailSuccess(true);
				setCodeInputs(['', '', '', '', '', '']);
				setEmailMFASetupCode('');
				setSelectedMFAMethod(null);
				setMfaToggleState(true);
				// Don't automatically show MFA section after enabling - user must click to see it
				// setShowMFASetup(true);
				await loadMFAStatus();
			}
		} catch (error) {
			setMfaError(intl.formatMessage({ id: 'ProfileErrorID32' }));
		} finally {
			setLoadingEmailMFA(false);
		}
	};

	// Password validation
	const validatePassword = password => {
		const errors = {};

		if (!password || password.trim() === '') {
			errors.password = intl.formatMessage({ id: 'ProfileErrorID33' });
			return errors;
		}

		if (password.length < 8) {
			errors.password = intl.formatMessage({ id: 'ProfileErrorID34' });
			return errors;
		}

		if (password.length > 128) {
			errors.password = intl.formatMessage({ id: 'ProfileErrorID35' });
			return errors;
		}

		if (!/[A-Z]/.test(password)) {
			errors.password = intl.formatMessage({ id: 'ProfileErrorID36' });
			return errors;
		}

		if (!/[a-z]/.test(password)) {
			errors.password = intl.formatMessage({ id: 'ProfileErrorID37' });
			return errors;
		}

		if (!/\d/.test(password)) {
			errors.password = intl.formatMessage({ id: 'ProfileErrorID38' });
			return errors;
		}

		if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
			errors.password = intl.formatMessage({ id: 'ProfileErrorID39' });
			return errors;
		}

		return errors;
	};

	const handleChangePassword = async () => {
		setPasswordErrors({});
		setUpdatingField('password');
		setSaveError('');
		setSaveSuccess('');

		// Validate current password
		if (!currentPassword || currentPassword.trim() === '') {
			setPasswordErrors({
				currentPassword: intl.formatMessage({ id: 'ProfileErrorID40' }),
			});
			setUpdatingField(null);
			return;
		}

		// Validate new password
		const newPasswordErrors = validatePassword(newPassword);
		if (Object.keys(newPasswordErrors).length > 0) {
			setPasswordErrors(newPasswordErrors);
			setUpdatingField(null);
			return;
		}

		// Validate confirm password
		if (newPassword !== confirmPassword) {
			setPasswordErrors({
				confirmPassword: intl.formatMessage({ id: 'ProfileErrorID41' }),
			});
			setUpdatingField(null);
			return;
		}

		try {
			const passwordData = {
				current_password: currentPassword,
				new_password: newPassword,
			};

			const result = await changePassword(passwordData);

			// Check if result is an error (axios error object)
			if (result && result.response) {
				const errorData = result.response.data;
				let errorMessage = intl.formatMessage({ id: 'ProfileErrorID42' });

				// Handle Pydantic validation errors (array format)
				if (
					errorData &&
					errorData.detail &&
					Array.isArray(errorData.detail) &&
					errorData.detail.length > 0
				) {
					errorMessage = errorData.detail[0].msg || errorMessage;
				} else if (errorData && errorData.message) {
					errorMessage = errorData.message;
				} else if (errorData && typeof errorData.detail === 'string') {
					errorMessage = errorData.detail;
				}

				setPasswordErrors({ general: errorMessage });
				setUpdatingField(null);
				return;
			}

			// Check if result is a successful response
			if (result && result.status === 200) {
				setSaveSuccess(intl.formatMessage({ id: 'ProfileErrorID43' }));
				setTimeout(() => setSaveSuccess(''), 3000);

				// Reset form
				setCurrentPassword('');
				setNewPassword('');
				setConfirmPassword('');
				setPasswordErrors({});
				setShowChangePasswordModal(false);
			} else {
				throw new Error(
					result?.message || intl.formatMessage({ id: 'ProfileErrorID44' }),
				);
			}
		} catch (error) {
			console.error('Change password error:', error);
			let errorMessage = intl.formatMessage({ id: 'ProfileErrorID45' });

			if (error.response?.data) {
				const errorData = error.response.data;
				if (
					errorData.detail &&
					Array.isArray(errorData.detail) &&
					errorData.detail.length > 0
				) {
					errorMessage = errorData.detail[0].msg || errorMessage;
				} else if (errorData.message) {
					errorMessage = errorData.message;
				} else if (typeof errorData.detail === 'string') {
					errorMessage = errorData.detail;
				}
			} else if (error.message) {
				errorMessage = error.message;
			}

			setPasswordErrors({ general: errorMessage });
		} finally {
			setUpdatingField(null);
		}
	};

	// Helper function to format date for display
	const formatDateForDisplay = dateString => {
		if (!dateString) return 'Not set';
		try {
			// If it's already in YYYY-MM-DD format, parse it directly
			if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
				const [year, month, day] = dateString.split('-');
				// Create date using local timezone but with the exact date values
				const date = new Date(
					parseInt(year),
					parseInt(month) - 1,
					parseInt(day),
				);
				if (isNaN(date.getTime())) return 'Not set';
				return date.toLocaleDateString('en-US', {
					year: 'numeric',
					month: 'long',
					day: 'numeric',
				});
			}

			// For other formats, try parsing normally
			const date = new Date(dateString);
			if (isNaN(date.getTime())) return 'Not set';
			return date.toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
			});
		} catch {
			return 'Not set';
		}
	};

	// Individual field update handlers
	const handleOpenEditModal = (field, currentValue) => {
		switch (field) {
			case 'name':
				setEditNameValue(currentValue || '');
				setShowEditNameModal(true);
				break;
			case 'dateOfBirth':
				setEditBirthDate(parseBirthDate(formatDateToYYYYMMDD(currentValue)));
				setShowEditDateOfBirthModal(true);
				break;
			case 'language':
				setEditLanguageValue(currentValue || 'English');
				setShowEditLanguageModal(true);
				break;
			case 'currency':
				setEditCurrencyValue(currentValue || 'USD');
				setShowEditCurrencyModal(true);
				break;
		}
	};

	const handleUpdateField = async (field, value) => {
		if (field === 'dateOfBirth' && !editBirthDate) return;
		setUpdatingField(field);
		setSaveError('');
		setSaveSuccess('');
		setFieldWithError(null);

		try {
			let updateData = {};

			switch (field) {
				case 'name':
					updateData = { full_name: value.trim() };
					break;
				case 'dateOfBirth':
					updateData = { date_of_birth: formatBirthDate(editBirthDate) };
					break;
				case 'language':
					updateData = { language_preference: languageMap[value] || 'en' };
					break;
				case 'currency':
					updateData = { currency_preference: value };
					break;
			}

			const result = await updateProfile(updateData);

			if (result && result.response) {
				const errorData = result.response.data;
				let errorMessage = intl.formatMessage({ id: 'ProfileErrorID45' });

				if (
					errorData &&
					errorData.detail &&
					Array.isArray(errorData.detail) &&
					errorData.detail.length > 0
				) {
					errorMessage = errorData.detail[0].msg || errorMessage;
					errorMessage = errorMessage.replace(/^Value error,\s*/i, '');
				} else if (errorData && errorData.message) {
					errorMessage = errorData.message;
				} else if (errorData && typeof errorData.detail === 'string') {
					errorMessage = errorData.detail;
				}

				setSaveError(errorMessage);
				setFieldWithError(field);
				setUpdatingField(null);
				return;
			}

			if (result && result.status === 200) {
				if (field === 'language') {
					const newLocale = languageMap[editLanguageValue] || 'en';
					handleSetLocale(newLocale);
					setFormData(prev => ({ ...prev, language: editLanguageValue }));
				}

				await fetchMe();
				setSaveSuccess(intl.formatMessage({ id: 'ProfileErrorID46' }));
				setTimeout(() => setSaveSuccess(''), 3000);

				setUpdatingField(null);
				setFieldWithError(null);

				switch (field) {
					case 'name':
						setShowEditNameModal(false);
						break;
					case 'dateOfBirth':
						setShowEditDateOfBirthModal(false);
						break;
					case 'language':
						setShowEditLanguageModal(false);
						break;
					case 'currency':
						setShowEditCurrencyModal(false);
						break;
				}
			} else {
				throw new Error(result?.message || 'Failed to update profile');
			}
		} catch (error) {
			console.error('Update field error (catch block):', error);
			let errorMessage = 'Failed to update profile';

			if (error.response?.data) {
				const errorData = error.response.data;

				if (
					errorData.detail &&
					Array.isArray(errorData.detail) &&
					errorData.detail.length > 0
				) {
					errorMessage = errorData.detail[0].msg || errorMessage;
					errorMessage = errorMessage.replace(/^Value error,\s*/i, '');
				} else if (errorData.message) {
					errorMessage = errorData.message;
				} else if (typeof errorData.detail === 'string') {
					errorMessage = errorData.detail;
				}
			} else if (error.message) {
				errorMessage = error.message;
			}

			setSaveError(errorMessage);
			setFieldWithError(field);
			setUpdatingField(null);
		}
	};
	if (isLoading) {
		return (
			<div className={styles.profilePage}>
				{/* <SidebarHeader />
        <main className={styles.mainContent}>
          <div className={styles.loadingContainer}>
            <div className={styles.loader}></div>
            <p>{intl.formatMessage({ id: "ProfileID1" })}</p>
          </div>
        </main> */}
				<Spinner />
			</div>
		);
	}

	// Main view (Google-style list)
	return (
		<div className={styles.profilePage}>
			{/* <SidebarHeader /> */}
			<main className={styles.mainContent}>
				<div className={styles.profileContainerNew}>
					{/* Profile Picture Section */}
					<div className={styles.profilePictureSection}>
						<div className={styles.profilePictureWrapper}>
							{getProfilePictureUrl(currentUser?.profile_picture) ? (
								<img
									src={getProfilePictureUrl(currentUser?.profile_picture)}
									alt='Profile'
									className={styles.profilePictureAvatar}
								/>
							) : (
								<div className={styles.profilePictureInitials}>
									{getInitials(formData.fullName || currentUser?.full_name)}
								</div>
							)}
							<button
								className={styles.profilePictureEditButton}
								onClick={handleImageClick}
								title='Change profile picture'
							>
								<Camera size={16} />
							</button>
						</div>
						<h2 className={styles.profilePictureName}>
							{formData.fullName || currentUser?.full_name || 'User'}
						</h2>
					</div>

					{/* Profile Fields List */}
					<div className={styles.profileFieldList}>
						{/* Name Field */}
						<div
							className={styles.profileFieldItem}
							onClick={() =>
								handleOpenEditModal(
									'name',
									formData.fullName || currentUser?.full_name,
								)
							}
						>
							<div className={styles.profileFieldIcon}>
								<User size={20} />
							</div>
							<div className={styles.profileFieldContent}>
								<div className={styles.profileFieldLabel}>
									{intl.formatMessage({ id: 'ProfileID2' })}
								</div>
								<div className={styles.profileFieldValue}>
									{formData.fullName || currentUser?.full_name || 'Not set'}
								</div>
							</div>
						</div>

						{/* Email Field */}
						<div
							className={`${styles.profileFieldItem} ${styles.profileFieldItemReadOnly}`}
						>
							<div className={styles.profileFieldIcon}>
								<Mail size={20} />
							</div>
							<div className={styles.profileFieldContent}>
								<div className={styles.profileFieldLabel}>
									{intl.formatMessage({ id: 'ProfileID4' })}
								</div>
								<div className={styles.profileFieldValue}>
									{formData.email ||
										currentUser?.email ||
										intl.formatMessage({ id: 'ProfileID5' })}
								</div>
							</div>
						</div>

						{/* Date of Birth Field */}
						<div
							className={styles.profileFieldItem}
							onClick={() =>
								handleOpenEditModal(
									'dateOfBirth',
									currentUser?.date_of_birth || formData.dateOfBirth,
								)
							}
						>
							<div className={styles.profileFieldIcon}>
								<Calendar size={20} />
							</div>
							<div className={styles.profileFieldContent}>
								<div className={styles.profileFieldLabel}>
									{intl.formatMessage({ id: 'ProfileID7' })}
								</div>
								<div className={styles.profileFieldValue}>
									{formatDateForDisplay(
										currentUser?.date_of_birth || formData.dateOfBirth,
									)}
								</div>
							</div>
						</div>

						{/* Language Field */}
						<div
							className={styles.profileFieldItem}
							onClick={() => handleOpenEditModal('language', formData.language)}
						>
							<div className={styles.profileFieldIcon}>
								<Globe size={20} />
							</div>
							<div className={styles.profileFieldContent}>
								<div className={styles.profileFieldLabel}>
									{intl.formatMessage({ id: 'ProfileID8' })}
								</div>
								<div className={styles.profileFieldValue}>
									{formData.language ||
										intl.formatMessage({ id: 'ProfileID9' })}
								</div>
							</div>
						</div>

						{/* Currency Field */}
						<div
							className={styles.profileFieldItem}
							onClick={() => handleOpenEditModal('currency', formData.currency)}
						>
							<div className={styles.profileFieldIcon}>
								<DollarSign size={20} />
							</div>
							<div className={styles.profileFieldContent}>
								<div className={styles.profileFieldLabel}>
									{intl.formatMessage({ id: 'ProfileID10' })}
								</div>
								<div className={styles.profileFieldValue}>
									{formData.currency || 'USD'}
								</div>
							</div>
						</div>

						{/* Password Change Field */}
						{!isGoogleUser() ? (
							<div
								className={styles.profileFieldItem}
								onClick={() => setShowChangePasswordModal(true)}
							>
								<div className={styles.profileFieldIcon}>
									<Lock size={20} />
								</div>
								<div className={styles.profileFieldContent}>
									<div className={styles.profileFieldLabel}>
										{intl.formatMessage({ id: 'ProfileID11' })}
									</div>
									<div className={styles.profileFieldValue}>••••••••</div>
								</div>
							</div>
						) : (
							<div
								className={`${styles.profileFieldItem} ${styles.profileFieldItemReadOnly}`}
							>
								<div className={styles.profileFieldIcon}>
									<Lock size={20} />
								</div>
								<div className={styles.profileFieldContent}>
									<div className={styles.profileFieldLabel}>
										{intl.formatMessage({ id: 'ProfileID11' })}
									</div>
									<div className={styles.profileFieldValue}>
										{intl.formatMessage({ id: 'ProfileID12' })}
									</div>
								</div>
							</div>
						)}

						{/* MFA Field */}
						<div className={styles.profileFieldItem} onClick={handleMFAToggle}>
							<div className={styles.profileFieldIcon}>
								<Shield size={20} />
							</div>
							<div className={styles.profileFieldContent}>
								<div className={styles.profileFieldLabel}>
									{intl.formatMessage({ id: 'ProfileID13' })}
								</div>
								<div className={styles.profileFieldValue}>
									{mfaStatus.totp_enabled || mfaStatus.email_mfa_enabled
										? intl.formatMessage({ id: 'ProfileID14' })
										: intl.formatMessage({ id: 'ProfileID15' })}
								</div>
							</div>
						</div>
					</div>

					{/* MFA Section (only show when user clicks - mfa_required is handled by setting showMFASetup) */}
					{showMFASetup && (
						<div className={styles.mfaSection} ref={mfaSectionRef}>
							<h3 className={styles.mfaTitle}>
								{intl.formatMessage({ id: 'ProfileID16' })}
							</h3>

							{mfaError && <div className={styles.mfaError}>{mfaError}</div>}
							{mfaSuccess && (
								<div className={styles.mfaSuccess}>{mfaSuccess}</div>
							)}

							<div className={styles.mfaItem}>
								<div className={styles.mfaItemHeader}>
									<div>
										<label className={styles.label}>
											{intl.formatMessage({ id: 'ProfileID17' })}
										</label>
										<span className={styles.mfaStatus}>
											{mfaStatus.totp_enabled
												? intl.formatMessage({ id: 'ProfileID18' })
												: intl.formatMessage({ id: 'ProfileID19' })}
										</span>
									</div>
									{mfaStatus.totp_enabled && (
										<button
											className={styles.mfaDisableButton}
											onClick={handleTOTPDisableClick}
											disabled={loadingMFA}
										>
											{intl.formatMessage({ id: 'ProfileID18' })}
										</button>
									)}
								</div>
								{!mfaStatus.totp_enabled && (
									<button
										className={styles.mfaSetupButton}
										onClick={handleEnableTOTPDirect}
										disabled={loadingMFA}
									>
										{loadingMFA
											? intl.formatMessage({ id: 'ProfileID19' })
											: intl.formatMessage({ id: 'ProfileID20' })}
									</button>
								)}
								{mfaStatus.totp_enabled &&
									mfaStatus.backup_codes_remaining > 0 && (
										<div className={styles.backupCodesInfo}>
											{mfaStatus.backup_codes_remaining}
											{intl.formatMessage({ id: 'ProfileID21' })}
										</div>
									)}
							</div>

							<div className={styles.mfaItem}>
								<div className={styles.mfaItemHeader}>
									<div>
										<label className={styles.label}>
											{intl.formatMessage({ id: 'ProfileID22' })}
										</label>
										<span className={styles.mfaStatus}>
											{mfaStatus.email_mfa_enabled
												? intl.formatMessage({ id: 'ProfileID23' })
												: intl.formatMessage({ id: 'ProfileID24' })}
										</span>
									</div>
									{mfaStatus.email_mfa_enabled && (
										<button
											className={styles.mfaDisableButton}
											onClick={handleEmailMFAToggle}
											disabled={loadingEmailMFA}
										>
											{intl.formatMessage({ id: 'ProfileID25' })}
										</button>
									)}
								</div>
								{!mfaStatus.email_mfa_enabled && (
									<button
										className={styles.mfaSetupButton}
										onClick={handleEnableEmailMFADirect}
										disabled={loadingEmailMFA}
									>
										{loadingEmailMFA
											? intl.formatMessage({ id: 'ProfileID26' })
											: intl.formatMessage({ id: 'ProfileID27' })}
									</button>
								)}
							</div>
						</div>
					)}
				</div>
			</main>

			{/* Individual Field Edit Modals */}

			{/* Edit Name Modal */}
			{showEditNameModal && (
				<div
					className={styles.fieldEditModal}
					onClick={() => {
						setShowEditNameModal(false);
						setUpdatingField(null);
						setSaveError('');
						setFieldWithError(null);
					}}
				>
					<div
						className={styles.fieldEditModalContent}
						onClick={e => e.stopPropagation()}
					>
						<div className={styles.fieldEditModalHeader}>
							<h3>{intl.formatMessage({ id: 'ProfileID28' })}</h3>
							<button
								onClick={() => {
									setShowEditNameModal(false);
									setUpdatingField(null);
									setSaveError('');
									setFieldWithError(null);
								}}
							>
								<X size={20} />
							</button>
						</div>
						<div className={styles.fieldEditModalBody}>
							<input
								type='text'
								value={editNameValue}
								onChange={e => {
									setEditNameValue(e.target.value);
									if (saveError && fieldWithError === 'name') {
										setSaveError('');
										setFieldWithError(null);
									}
								}}
								className={styles.fieldEditInput}
								placeholder={intl.formatMessage({ id: 'ProfileID29' })}
								autoFocus
							/>
							{saveError && fieldWithError === 'name' && (
								<div className={styles.fieldEditError}>{saveError}</div>
							)}
						</div>
						<div className={styles.fieldEditModalFooter}>
							<button
								className={styles.fieldEditCancelButton}
								onClick={() => {
									setShowEditNameModal(false);
									setUpdatingField(null);
									setSaveError('');
									setFieldWithError(null);
								}}
							>
								{intl.formatMessage({ id: 'ProfileID30' })}
							</button>
							<button
								className={styles.fieldEditSaveButton}
								onClick={() => handleUpdateField('name', editNameValue)}
								disabled={updatingField === 'name' || !editNameValue.trim()}
							>
								{updatingField === 'name'
									? intl.formatMessage({ id: 'ProfileID31' })
									: intl.formatMessage({ id: 'ProfileID32' })}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Edit Date of Birth Modal */}
			{showEditDateOfBirthModal && (
				<div
					className={styles.fieldEditModal}
					onClick={() => {
						setShowEditDateOfBirthModal(false);
						setUpdatingField(null);
						setSaveError('');
						setFieldWithError(null);
					}}
				>
					<div
						className={styles.fieldEditModalContent}
						onClick={e => e.stopPropagation()}
					>
						<div className={styles.fieldEditModalHeader}>
							<h3>{intl.formatMessage({ id: 'ProfileID38' })}</h3>
							<button
								onClick={() => {
									setShowEditDateOfBirthModal(false);
									setUpdatingField(null);
									setSaveError('');
									setFieldWithError(null);
								}}
							>
								<X size={20} />
							</button>
						</div>
						<div className={styles.fieldEditModalBody}>
							<div className={styles.birthdayPickerWrapper}>
								<label className={styles.birthdayLabel}>
									{intl.formatMessage({ id: 'ProfileID7' })}
								</label>
								<div className={styles.birthdayDateField}>
									<Calendar size={18} className={styles.birthdayFieldIcon} />
									<DatePicker
										selected={editBirthDate}
										onChange={date => {
											setEditBirthDate(date);
											if (saveError && fieldWithError === 'dateOfBirth') {
												setSaveError('');
												setFieldWithError(null);
											}
										}}
										dateFormat='dd.MM.yyyy'
										placeholderText={intl.formatMessage({ id: 'ProfileID148' })}
										locale={datePickerLocale(intl.locale)}
										minDate={BIRTH_MIN_DATE}
										maxDate={new Date()}
										showMonthDropdown
										showYearDropdown
										dropdownMode='scroll'
										scrollableYearDropdown
										yearDropdownItemNumber={130}
										calendarStartDay={1}
										popperPlacement='bottom-start'
										showPopperArrow={false}
										calendarClassName='fpCalendar'
										popperClassName='fpCalendarPopper'
										className={styles.birthdayDateInput}
										wrapperClassName={styles.birthdayDatePickerWrapper}
										autoFocus
									/>
								</div>
							</div>
							{saveError && fieldWithError === 'dateOfBirth' && (
								<div className={styles.fieldEditError}>{saveError}</div>
							)}
						</div>
						<div className={styles.fieldEditModalFooter}>
							<button
								className={styles.fieldEditCancelButton}
								onClick={() => {
									setShowEditDateOfBirthModal(false);
									setUpdatingField(null);
									setSaveError('');
									setFieldWithError(null);
								}}
							>
								{intl.formatMessage({ id: 'ProfileID55' })}
							</button>
							<button
								className={styles.fieldEditSaveButton}
								onClick={() => handleUpdateField('dateOfBirth', null)}
								disabled={updatingField === 'dateOfBirth' || !editBirthDate}
							>
								{updatingField === 'dateOfBirth'
									? intl.formatMessage({ id: 'ProfileID56' })
									: intl.formatMessage({ id: 'ProfileID57' })}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Edit Language Modal */}
			{showEditLanguageModal && (
				<div
					className={styles.fieldEditModal}
					onClick={() => {
						setShowEditLanguageModal(false);
						setUpdatingField(null);
						setSaveError('');
						setFieldWithError(null);
					}}
				>
					<div
						className={styles.fieldEditModalContent}
						onClick={e => e.stopPropagation()}
					>
						<div className={styles.fieldEditModalHeader}>
							<h3>{intl.formatMessage({ id: 'ProfileID58' })}</h3>
							<button
								onClick={() => {
									setShowEditLanguageModal(false);
									setUpdatingField(null);
									setSaveError('');
									setFieldWithError(null);
								}}
							>
								<X size={20} />
							</button>
						</div>
						<div className={styles.fieldEditModalBody}>
							<select
								value={editLanguageValue}
								onChange={e => {
									setEditLanguageValue(e.target.value);
									if (saveError && fieldWithError === 'language') {
										setSaveError('');
										setFieldWithError(null);
									}
								}}
								className={styles.fieldEditSelect}
								autoFocus
							>
								<option value='English'>
									{intl.formatMessage({ id: 'ProfileID59' })}
								</option>
								<option value='Armenian'>
									{intl.formatMessage({ id: 'ProfileID60' })}
								</option>
								<option value='Russian'>
									{intl.formatMessage({ id: 'ProfileID61' })}
								</option>
							</select>
							{saveError && fieldWithError === 'language' && (
								<div className={styles.fieldEditError}>{saveError}</div>
							)}
						</div>
						<div className={styles.fieldEditModalFooter}>
							<button
								className={styles.fieldEditCancelButton}
								onClick={() => {
									setShowEditLanguageModal(false);
									setUpdatingField(null);
									setSaveError('');
									setFieldWithError(null);
								}}
							>
								{intl.formatMessage({ id: 'ProfileID62' })}
							</button>
							<button
								className={styles.fieldEditSaveButton}
								onClick={() => handleUpdateField('language', editLanguageValue)}
								disabled={updatingField === 'language'}
							>
								{updatingField === 'language'
									? intl.formatMessage({ id: 'ProfileID63' })
									: intl.formatMessage({ id: 'ProfileID64' })}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Change Password Modal */}
			{showChangePasswordModal && (
				<div
					className={styles.fieldEditModal}
					onClick={() => {
						setShowChangePasswordModal(false);
						setUpdatingField(null);
						setCurrentPassword('');
						setNewPassword('');
						setConfirmPassword('');
						setPasswordErrors({});
						setSaveError('');
					}}
				>
					<div
						className={styles.fieldEditModalContent}
						onClick={e => e.stopPropagation()}
					>
						<div className={styles.fieldEditModalHeader}>
							<h3>{intl.formatMessage({ id: 'ProfileID65' })}</h3>
							<button
								onClick={() => {
									setShowChangePasswordModal(false);
									setUpdatingField(null);
									setCurrentPassword('');
									setNewPassword('');
									setConfirmPassword('');
									setPasswordErrors({});
									setSaveError('');
								}}
							>
								<X size={20} />
							</button>
						</div>
						<div className={styles.fieldEditModalBody}>
							<div className={styles.passwordInputGroup}>
								<label className={styles.passwordLabel}>
									{intl.formatMessage({ id: 'ProfileID66' })}
								</label>
								<div className={styles.passwordInputWrapper}>
									<input
										type={showCurrentPassword ? 'text' : 'password'}
										value={currentPassword}
										onChange={e => {
											setCurrentPassword(e.target.value);
											if (passwordErrors.currentPassword) {
												setPasswordErrors(prev => ({
													...prev,
													currentPassword: '',
												}));
											}
										}}
										className={styles.passwordInput}
										placeholder={intl.formatMessage({ id: 'ProfileID67' })}
										autoFocus
									/>
									<button
										type='button'
										className={styles.passwordToggle}
										onClick={() => setShowCurrentPassword(!showCurrentPassword)}
									>
										{showCurrentPassword ? (
											<EyeOff size={18} />
										) : (
											<Eye size={18} />
										)}
									</button>
								</div>
								{passwordErrors.currentPassword && (
									<div className={styles.fieldEditError}>
										{passwordErrors.currentPassword}
									</div>
								)}
							</div>

							<div className={styles.passwordInputGroup}>
								<label className={styles.passwordLabel}>
									{intl.formatMessage({ id: 'ProfileID68' })}
								</label>
								<div className={styles.passwordInputWrapper}>
									<input
										type={showNewPassword ? 'text' : 'password'}
										value={newPassword}
										onChange={e => {
											setNewPassword(e.target.value);
											if (passwordErrors.password) {
												setPasswordErrors(prev => ({
													...prev,
													password: '',
												}));
											}
										}}
										className={styles.passwordInput}
										placeholder={intl.formatMessage({ id: 'ProfileID69' })}
									/>
									<button
										type='button'
										className={styles.passwordToggle}
										onClick={() => setShowNewPassword(!showNewPassword)}
									>
										{showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
									</button>
								</div>
								{passwordErrors.password && (
									<div className={styles.fieldEditError}>
										{passwordErrors.password}
									</div>
								)}
							</div>

							<div className={styles.passwordInputGroup}>
								<label className={styles.passwordLabel}>
									{intl.formatMessage({ id: 'ProfileID70' })}
								</label>
								<div className={styles.passwordInputWrapper}>
									<input
										type={showConfirmPassword ? 'text' : 'password'}
										value={confirmPassword}
										onChange={e => {
											setConfirmPassword(e.target.value);
											if (passwordErrors.confirmPassword) {
												setPasswordErrors(prev => ({
													...prev,
													confirmPassword: '',
												}));
											}
										}}
										className={styles.passwordInput}
										placeholder={intl.formatMessage({ id: 'ProfileID70' })}
									/>
									<button
										type='button'
										className={styles.passwordToggle}
										onClick={() => setShowConfirmPassword(!showConfirmPassword)}
									>
										{showConfirmPassword ? (
											<EyeOff size={18} />
										) : (
											<Eye size={18} />
										)}
									</button>
								</div>
								{passwordErrors.confirmPassword && (
									<div className={styles.fieldEditError}>
										{passwordErrors.confirmPassword}
									</div>
								)}
							</div>

							{passwordErrors.general && (
								<div className={styles.fieldEditError}>
									{passwordErrors.general}
								</div>
							)}
							{saveSuccess && updatingField === 'password' && (
								<div className={styles.fieldEditSuccess}>{saveSuccess}</div>
							)}
						</div>
						<div className={styles.fieldEditModalFooter}>
							<button
								className={styles.fieldEditCancelButton}
								onClick={() => {
									setShowChangePasswordModal(false);
									setUpdatingField(null);
									setCurrentPassword('');
									setNewPassword('');
									setConfirmPassword('');
									setPasswordErrors({});
									setSaveError('');
								}}
							>
								{intl.formatMessage({ id: 'ProfileID62' })}
							</button>
							<button
								className={styles.fieldEditSaveButton}
								onClick={handleChangePassword}
								disabled={
									updatingField === 'password' ||
									!currentPassword ||
									!newPassword ||
									!confirmPassword
								}
							>
								{updatingField === 'password'
									? intl.formatMessage({ id: 'ProfileID71' })
									: intl.formatMessage({ id: 'ProfileID72' })}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Edit Currency Modal */}
			{showEditCurrencyModal && (
				<div
					className={styles.fieldEditModal}
					onClick={() => {
						setShowEditCurrencyModal(false);
						setUpdatingField(null);
						setSaveError('');
						setFieldWithError(null);
					}}
				>
					<div
						className={styles.fieldEditModalContent}
						onClick={e => e.stopPropagation()}
					>
						<div className={styles.fieldEditModalHeader}>
							<h3>{intl.formatMessage({ id: 'ProfileID73' })}</h3>
							<button
								onClick={() => {
									setShowEditCurrencyModal(false);
									setUpdatingField(null);
									setSaveError('');
									setFieldWithError(null);
								}}
							>
								<X size={20} />
							</button>
						</div>
						<div className={styles.fieldEditModalBody}>
							<select
								value={editCurrencyValue}
								onChange={e => {
									setEditCurrencyValue(e.target.value);
									if (saveError && fieldWithError === 'currency') {
										setSaveError('');
										setFieldWithError(null);
									}
								}}
								className={styles.fieldEditSelect}
								autoFocus
							>
								{SUPPORTED_CURRENCIES_WITH_FLAGS.map(c => (
									<option key={c.code} value={c.code}>
										{c.flag} {c.code}
									</option>
								))}
							</select>
							{saveError && fieldWithError === 'currency' && (
								<div className={styles.fieldEditError}>{saveError}</div>
							)}
						</div>
						<div className={styles.fieldEditModalFooter}>
							<button
								className={styles.fieldEditCancelButton}
								onClick={() => {
									setShowEditCurrencyModal(false);
									setUpdatingField(null);
									setSaveError('');
									setFieldWithError(null);
								}}
							>
								{intl.formatMessage({ id: 'ProfileID74' })}
							</button>
							<button
								className={styles.fieldEditSaveButton}
								onClick={() => handleUpdateField('currency', editCurrencyValue)}
								disabled={updatingField === 'currency'}
							>
								{updatingField === 'currency'
									? intl.formatMessage({ id: 'ProfileID75' })
									: intl.formatMessage({ id: 'ProfileID76' })}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Profile Picture Modal */}
			{showImageModal && (
				<div className={styles.imageModal}>
					<div className={styles.imageModalContent}>
						<div className={styles.imageModalHeader}>
							<h3 className={styles.imageModalTitle}>
								{intl.formatMessage({ id: 'ProfileID77' })}
							</h3>
							<button
								className={styles.closeButton}
								onClick={() => {
									if (!selectedImage) {
										const currentPictureUrl = getProfilePictureUrl(
											currentUser?.profile_picture,
										);
										setImagePreview(currentPictureUrl || null);
									}
									setShowImageModal(false);
								}}
								disabled={isUploadingPicture || isDeletingPicture}
							>
								<X size={20} />
							</button>
						</div>

						<div className={styles.imagePreviewContainer}>
							{imagePreview ? (
								<img
									src={imagePreview}
									alt='Preview'
									className={styles.imagePreview}
								/>
							) : (
								<div className={styles.noPreview}>
									<Camera size={40} />
									<p>{intl.formatMessage({ id: 'ProfileID78' })}</p>
								</div>
							)}
						</div>

						<div className={styles.imageModalActions}>
							<input
								type='file'
								ref={fileInputRef}
								onChange={handleFileSelect}
								accept='image/jpeg,image/jpg,image/png,image/gif'
								className={styles.fileInput}
								id='profile-picture-input'
								disabled={isUploadingPicture || isDeletingPicture}
							/>

							<div className={styles.buttonGroup}>
								<label
									htmlFor='profile-picture-input'
									className={styles.browseButton}
									style={{
										cursor:
											isUploadingPicture || isDeletingPicture
												? 'not-allowed'
												: 'pointer',
										opacity: isUploadingPicture || isDeletingPicture ? 0.6 : 1,
										pointerEvents:
											isUploadingPicture || isDeletingPicture ? 'none' : 'auto',
									}}
								>
									<Upload size={16} />
									{intl.formatMessage({ id: 'ProfileID79' })}
								</label>

								{selectedImage && (
									<button
										className={styles.removeButton}
										onClick={() => {
											setSelectedImage(null);
											const currentPictureUrl = getProfilePictureUrl(
												currentUser?.profile_picture,
											);
											setImagePreview(currentPictureUrl || null);
										}}
										disabled={isUploadingPicture || isDeletingPicture}
									>
										{intl.formatMessage({ id: 'ProfileID80' })}
									</button>
								)}
							</div>

							<div className={styles.fileInfo}>
								<p className={styles.fileRequirements}>
									{intl.formatMessage({ id: 'ProfileID81' })}
								</p>
							</div>

							<div className={styles.modalButtons}>
								<button
									className={styles.doneButton}
									onClick={handleDoneInModal}
									disabled={isUploadingPicture || isDeletingPicture}
								>
									{isUploadingPicture
										? intl.formatMessage({ id: 'ProfileID82' })
										: intl.formatMessage({ id: 'ProfileID83' })}
								</button>
							</div>

							{currentUser?.profile_picture && (
								<div className={styles.removeCurrentPhotoContainer}>
									<button
										className={styles.removeCurrentPhotoButton}
										onClick={handleDeleteProfilePicture}
										disabled={isDeletingPicture}
									>
										{isDeletingPicture
											? intl.formatMessage({ id: 'ProfileID84' })
											: intl.formatMessage({ id: 'ProfileID86' })}
									</button>
								</div>
							)}
						</div>
					</div>
				</div>
			)}

			{/* Delete Photo Confirmation Modal */}
			{showDeletePhotoConfirm && (
				<div
					className={styles.deleteConfirmOverlay}
					onClick={handleCancelDeletePhoto}
				>
					<div
						className={styles.deleteConfirmModal}
						onClick={e => e.stopPropagation()}
					>
						<AlertTriangle size={48} className={styles.deleteWarningIcon} />
						<h3 className={styles.deleteConfirmTitle}>
							{intl.formatMessage({ id: 'ProfileID146' })}
						</h3>
						<p className={styles.deleteConfirmMessage}>
							{intl.formatMessage({ id: 'ProfileID147' })}
						</p>
						<div className={styles.deleteConfirmButtons}>
							<button
								className={styles.deleteConfirmButton}
								onClick={handleConfirmDeletePhoto}
								disabled={isDeletingPicture}
							>
								{isDeletingPicture
									? intl.formatMessage({ id: 'ProfileID87' })
									: intl.formatMessage({ id: 'ProfileID88' })}
							</button>
							<button
								className={styles.deleteCancelButton}
								onClick={handleCancelDeletePhoto}
								disabled={isDeletingPicture}
							>
								{intl.formatMessage({ id: 'ProfileID89' })}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Method Selection Modal - Render outside conditional so it's always available */}
			{showMFAMethodSelection && (
				<div className={styles.mfaModal}>
					<div className={styles.mfaModalContent}>
						<div className={styles.mfaModalHeader}>
							<h3 className={styles.mfaModalTitle}>
								{intl.formatMessage({ id: 'ProfileID90' })}
							</h3>
							<button
								className={styles.mfaModalClose}
								onClick={() => {
									setShowMFAMethodSelection(false);
									setSelectedMFAMethod(null);
									setMfaError('');
								}}
							>
								<X size={20} />
							</button>
						</div>
						<p className={styles.mfaModalDescription}>
							{intl.formatMessage({ id: 'ProfileID91' })}
						</p>
						<div className={styles.mfaMethodSelection}>
							<div
								className={styles.mfaMethodOption}
								onClick={() => handleSelectMFAMethod('totp')}
							>
								<div className={styles.mfaMethodHeader}>
									<h4 className={styles.mfaMethodTitle}>
										{intl.formatMessage({ id: 'ProfileID92' })}
									</h4>
								</div>
								<p className={styles.mfaMethodDescription}>
									{intl.formatMessage({ id: 'ProfileID93' })}
								</p>
								<button className={styles.mfaMethodLink}>
									{intl.formatMessage({ id: 'ProfileID94' })}
								</button>
								<span className={styles.mfaMethodBadge}>
									{intl.formatMessage({ id: 'ProfileID95' })}
								</span>
							</div>
							<div
								className={styles.mfaMethodOption}
								onClick={() => handleSelectMFAMethod('email')}
							>
								<div className={styles.mfaMethodHeader}>
									<h4 className={styles.mfaMethodTitle}>
										{intl.formatMessage({ id: 'ProfileID96' })}
									</h4>
								</div>
								<p className={styles.mfaMethodDescription}>
									{intl.formatMessage({ id: 'ProfileID97' })}
								</p>
								<button className={styles.mfaMethodLink}>
									{intl.formatMessage({ id: 'ProfileID98' })}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Email Verification Modal */}
			{showEmailMFASetup && selectedMFAMethod === 'email' && (
				<div className={styles.mfaModal}>
					<div className={styles.mfaModalContent}>
						<div className={styles.mfaModalHeader}>
							<h3 className={styles.mfaModalTitle}>
								{intl.formatMessage({ id: 'ProfileID99' })}
							</h3>
							<button
								className={styles.mfaModalClose}
								onClick={() => {
									setShowEmailMFASetup(false);
									setSelectedMFAMethod(null);
									setCodeInputs(['', '', '', '', '', '']);
									setEmailMFASetupCode('');
									setMfaError('');
								}}
							>
								<X size={20} />
							</button>
						</div>
						<h4 className={styles.mfaModalSubtitle}>
							{intl.formatMessage({ id: 'ProfileID100' })}
						</h4>
						<p className={styles.mfaModalDescription}>
							{intl.formatMessage({ id: 'ProfileID101' })}
							{currentUser?.email || intl.formatMessage({ id: 'ProfileID102' })}
							. {intl.formatMessage({ id: 'ProfileID103' })}
						</p>
						<div className={styles.codeInputContainer}>
							{codeInputs.map((value, index) => (
								<input
									key={index}
									id={`code-input-${index}`}
									type='text'
									inputMode='numeric'
									maxLength={1}
									value={value}
									onChange={e => handleCodeInputChange(index, e.target.value)}
									onKeyDown={e => handleCodeInputKeyDown(index, e)}
									onPaste={handlePasteCode}
									className={styles.codeInput}
									autoFocus={index === 0}
								/>
							))}
						</div>
						{mfaError && <div className={styles.mfaError}>{mfaError}</div>}
						<button
							className={styles.mfaVerifyButton}
							onClick={handleEmailMFASetupVerify}
							disabled={loadingEmailMFA || codeInputs.join('').length !== 6}
						>
							{loadingEmailMFA
								? intl.formatMessage({ id: 'ProfileID104' })
								: intl.formatMessage({ id: 'ProfileID105' })}
						</button>
						<div className={styles.mfaResendContainer}>
							<span className={styles.mfaResendText}>
								{intl.formatMessage({ id: 'ProfileID106' })}
								{String(Math.floor(emailResendCountdown / 60)).padStart(2, '0')}
								:{String(emailResendCountdown % 60).padStart(2, '0')}
							</span>
							<button
								className={`${styles.mfaResendButton} ${
									emailResendCountdown > 0 ? styles.mfaResendButtonDisabled : ''
								}`}
								onClick={handleResendEmailCode}
								disabled={emailResendCountdown > 0 || loadingEmailMFA}
							>
								{intl.formatMessage({ id: 'ProfileID107' })}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Email Success Modal */}
			{showEmailSuccess && (
				<div className={styles.mfaModal}>
					<div className={styles.mfaModalContent}>
						<div className={styles.mfaModalHeader}>
							<h3 className={styles.mfaModalTitle}>
								{intl.formatMessage({ id: 'ProfileID108' })}
							</h3>
							<button
								className={styles.mfaModalClose}
								onClick={() => {
									setShowEmailSuccess(false);
									setSelectedMFAMethod(null);
								}}
							>
								<X size={20} />
							</button>
						</div>
						<div className={styles.mfaSuccessIcon}>
							<div className={styles.mfaSuccessCheckmark}>✓</div>
						</div>
						<p className={styles.mfaSuccessMessage}>
							{intl.formatMessage({ id: 'ProfileID109' })}
						</p>
						<button
							className={styles.mfaVerifyButton}
							onClick={() => {
								setShowEmailSuccess(false);
								setSelectedMFAMethod(null);
								setShowMFAMethodSelection(false);
							}}
						>
							{intl.formatMessage({ id: 'ProfileID110' })}
						</button>
					</div>
				</div>
			)}

			{/* TOTP Setup Modal - QR Code View */}
			{showTOTPSetup &&
				totpSetupData &&
				selectedMFAMethod === 'totp' &&
				!showSecretKey &&
				!showTOTPVerification && (
					<div className={styles.mfaModal}>
						<div className={styles.mfaModalContent}>
							<div className={styles.mfaModalHeader}>
								<h3 className={styles.mfaModalTitle}>
									{intl.formatMessage({ id: 'ProfileID111' })}
								</h3>
								<button
									className={styles.mfaModalClose}
									onClick={() => {
										setShowTOTPSetup(false);
										setTotpSetupData(null);
										setSelectedMFAMethod(null);
										setMfaError('');
									}}
								>
									<X size={20} />
								</button>
							</div>
							<h4 className={styles.mfaModalSubtitle}>
								{intl.formatMessage({ id: 'ProfileID112' })}
							</h4>
							<p className={styles.mfaModalDescription}>
								{intl.formatMessage({ id: 'ProfileID113' })}
							</p>
							<div className={styles.qrCodeContainer}>
								<img
									src={totpSetupData.qr_code_image}
									alt='TOTP QR Code'
									className={styles.qrCode}
								/>
							</div>
							<p className={styles.mfaSecretKeyLink}>
								{intl.formatMessage({ id: 'ProfileID114' })}{' '}
								<button
									className={styles.mfaSecretKeyButton}
									onClick={() => setShowSecretKey(true)}
								>
									{intl.formatMessage({ id: 'ProfileID115' })}
								</button>
							</p>
							<button
								className={styles.mfaVerifyButton}
								onClick={() => {
									setShowTOTPVerification(true);
									setShowTOTPSetup(false);
									setCodeInputs(['', '', '', '', '', '']);
								}}
							>
								{intl.formatMessage({ id: 'ProfileID116' })}
							</button>
						</div>
					</div>
				)}

			{/* TOTP Setup Modal - Secret Key View */}
			{showTOTPSetup &&
				totpSetupData &&
				selectedMFAMethod === 'totp' &&
				showSecretKey &&
				!showTOTPVerification && (
					<div className={styles.mfaModal}>
						<div className={styles.mfaModalContent}>
							<div className={styles.mfaModalHeader}>
								<h3 className={styles.mfaModalTitle}>
									{intl.formatMessage({ id: 'ProfileID117' })}
								</h3>
								<button
									className={styles.mfaModalClose}
									onClick={() => {
										setShowTOTPSetup(false);
										setTotpSetupData(null);
										setSelectedMFAMethod(null);
										setShowSecretKey(false);
										setMfaError('');
									}}
								>
									<X size={20} />
								</button>
							</div>
							<h4 className={styles.mfaModalSubtitle}>
								{intl.formatMessage({ id: 'ProfileID118' })}
							</h4>
							<p className={styles.mfaModalDescription}>
								{intl.formatMessage({ id: 'ProfileID119' })}
							</p>
							<div className={styles.qrCodeContainer}>
								<img
									src={totpSetupData.qr_code_image}
									alt='TOTP QR Code'
									className={styles.qrCode}
								/>
							</div>
							<p className={styles.mfaSecretKeyLink}>
								{intl.formatMessage({ id: 'ProfileID120' })}{' '}
								<button
									className={styles.mfaSecretKeyButton}
									onClick={() => setShowSecretKey(true)}
								>
									{intl.formatMessage({ id: 'ProfileID121' })}
								</button>
							</p>
							<div className={styles.mfaSecretKeySection}>
								<h5 className={styles.mfaSecretKeyTitle}>
									{intl.formatMessage({ id: 'ProfileID122' })}
								</h5>
								<p className={styles.mfaSecretKeyInstruction}>
									{intl.formatMessage({ id: 'ProfileID123' })}
								</p>
								<div className={styles.mfaSecretKeyDisplay}>
									<code className={styles.mfaSecretKeyValue}>
										{totpSetupData.secret}
									</code>
									<button
										className={styles.mfaCopyButton}
										onClick={copySecretKey}
									>
										{intl.formatMessage({ id: 'ProfileID124' })}
									</button>
								</div>
								<p className={styles.mfaSecretKeyNote}>
									{intl.formatMessage({ id: 'ProfileID125' })}
								</p>
							</div>
							<button
								className={styles.mfaVerifyButton}
								onClick={() => {
									setShowTOTPVerification(true);
									setShowTOTPSetup(false);
									setCodeInputs(['', '', '', '', '', '']);
								}}
							>
								{intl.formatMessage({ id: 'ProfileID126' })}
							</button>
						</div>
					</div>
				)}

			{/* TOTP Verification Modal */}
			{showTOTPVerification &&
				totpSetupData &&
				selectedMFAMethod === 'totp' && (
					<div className={styles.mfaModal}>
						<div className={styles.mfaModalContent}>
							<div className={styles.mfaModalHeader}>
								<h3 className={styles.mfaModalTitle}>
									{intl.formatMessage({ id: 'ProfileID127' })}
								</h3>
								<button
									className={styles.mfaModalClose}
									onClick={() => {
										setShowTOTPVerification(false);
										setTotpSetupData(null);
										setSelectedMFAMethod(null);
										setCodeInputs(['', '', '', '', '', '']);
										setMfaError('');
									}}
								>
									<X size={20} />
								</button>
							</div>
							<h4 className={styles.mfaModalSubtitle}>
								{intl.formatMessage({ id: 'ProfileID128' })}
							</h4>
							<p className={styles.mfaModalDescription}>
								{intl.formatMessage({ id: 'ProfileID129' })}
							</p>
							<div className={styles.codeInputContainer}>
								{codeInputs.map((value, index) => (
									<input
										key={index}
										id={`code-input-${index}`}
										type='text'
										inputMode='numeric'
										maxLength={1}
										value={value}
										onChange={e => handleCodeInputChange(index, e.target.value)}
										onKeyDown={e => handleCodeInputKeyDown(index, e)}
										onPaste={handlePasteCode}
										className={styles.codeInput}
										autoFocus={index === 0}
									/>
								))}
							</div>
							<p className={styles.mfaCodeInfo}>
								{intl.formatMessage({ id: 'ProfileID130' })}
							</p>
							{mfaError && <div className={styles.mfaError}>{mfaError}</div>}
							<button
								className={styles.mfaVerifyButton}
								onClick={handleTOTPVerify}
								disabled={loadingMFA || codeInputs.join('').length !== 6}
							>
								{loadingMFA
									? intl.formatMessage({ id: 'ProfileID131' })
									: intl.formatMessage({ id: 'ProfileID132' })}
							</button>
						</div>
					</div>
				)}

			{/* TOTP Success Modal with Backup Codes */}
			{showTOTPSuccess && totpBackupCodes && (
				<div className={styles.mfaModal}>
					<div className={styles.mfaModalContent}>
						<div className={styles.mfaModalHeader}>
							<h3 className={styles.mfaModalTitle}>
								{intl.formatMessage({ id: 'ProfileID133' })}
							</h3>
							<button
								className={styles.mfaModalClose}
								onClick={() => {
									setShowTOTPSuccess(false);
									setTotpBackupCodes(null);
									setSelectedMFAMethod(null);
								}}
							>
								<X size={20} />
							</button>
						</div>
						<div className={styles.mfaSuccessIcon}>
							<div className={styles.mfaSuccessCheckmark}>✓</div>
						</div>
						<p className={styles.mfaSuccessMessage}>
							{intl.formatMessage({ id: 'ProfileID134' })}
						</p>
						<p className={styles.mfaBackupCodesTitle}>
							{intl.formatMessage({ id: 'ProfileID135' })}
						</p>
						<div className={styles.mfaBackupCodesGrid}>
							{totpBackupCodes.map((code, index) => (
								<div key={index} className={styles.mfaBackupCodeItem}>
									{code}
								</div>
							))}
						</div>
						<button
							className={styles.mfaVerifyButton}
							onClick={() => {
								setShowTOTPSuccess(false);
								setTotpBackupCodes(null);
								setSelectedMFAMethod(null);
								setShowMFAMethodSelection(false);
							}}
						>
							{intl.formatMessage({ id: 'ProfileID136' })}
						</button>
					</div>
				</div>
			)}

			{showTOTPDisable && (
				<div className={styles.mfaModal}>
					<div className={styles.mfaModalContent}>
						<div className={styles.mfaModalHeader}>
							<h3 className={styles.mfaModalTitle}>
								{intl.formatMessage({ id: 'ProfileID137' })}
							</h3>
							<button
								className={styles.mfaModalClose}
								onClick={() => {
									setShowTOTPDisable(false);
									setTotpDisableCode('');
									setTotpDisableCodeInputs(['', '', '', '', '', '']);
									setMfaError('');
								}}
							>
								<X size={20} />
							</button>
						</div>
						<h4 className={styles.mfaModalSubtitle}>
							{intl.formatMessage({ id: 'ProfileID138' })}
						</h4>
						<p className={styles.mfaModalDescription}>
							{intl.formatMessage({ id: 'ProfileID139' })}
						</p>
						<div className={styles.codeInputContainer}>
							{totpDisableCodeInputs.map((value, index) => (
								<input
									key={index}
									id={`disable-code-input-${index}`}
									type='text'
									inputMode='numeric'
									maxLength={1}
									value={value}
									onChange={e =>
										handleTOTPDisableCodeInputChange(index, e.target.value)
									}
									onKeyDown={e => handleTOTPDisableCodeInputKeyDown(index, e)}
									onPaste={handleTOTPDisablePasteCode}
									className={styles.codeInput}
									autoFocus={index === 0}
								/>
							))}
						</div>
						{mfaError && <div className={styles.mfaError}>{mfaError}</div>}
						<div className={styles.mfaModalButtons}>
							<button
								className={styles.mfaVerifyButton}
								onClick={handleTOTPDisable}
								disabled={
									loadingMFA || totpDisableCodeInputs.join('').length !== 6
								}
							>
								{loadingMFA
									? intl.formatMessage({ id: 'ProfileID140' })
									: intl.formatMessage({ id: 'ProfileID141' })}
							</button>
							<button
								className={styles.mfaCancelButton}
								onClick={() => {
									setShowTOTPDisable(false);
									setTotpDisableCode('');
									setTotpDisableCodeInputs(['', '', '', '', '', '']);
									setMfaError('');
								}}
								disabled={loadingMFA}
							>
								{intl.formatMessage({ id: 'ProfileID142' })}
							</button>
						</div>
					</div>
				</div>
			)}

			{totpBackupCodes && !showTOTPSuccess && (
				<div className={styles.backupCodesModal}>
					<div className={styles.backupCodesContent}>
						<h4 className={styles.backupCodesTitle}>
							{intl.formatMessage({ id: 'ProfileID143' })}
						</h4>
						<p className={styles.backupCodesWarning}>
							{intl.formatMessage({ id: 'ProfileID144' })}
						</p>
						<div className={styles.backupCodesList}>
							{totpBackupCodes.map((code, index) => (
								<div key={index} className={styles.backupCodeItem}>
									{code}
								</div>
							))}
						</div>
						<button
							className={styles.submitButton}
							onClick={() => setTotpBackupCodes(null)}
						>
							{intl.formatMessage({ id: 'ProfileID145' })}
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default Profile;
